import { type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger, cn } from "@lumika/ui";

export type NavGroup = "home" | "work" | "business" | "settings";

export type NavItem = {
  to: string;
  label: string;
  icon: ReactNode;
  group: NavGroup;
  /**
   * Items not in the bottom-nav primary set still live in the side drawer.
   * Keeps the bottom row honest (≤4 destinations) while preserving access.
   */
  primary?: boolean;
  /** One-line plain-language description shown as a hover tooltip. */
  hint?: string;
};

// Group headers shown at the seam between groups (only for named groups).
const GROUP_LABELS: Partial<Record<NavGroup, string>> = {
  work: "Work",
  business: "Business",
};

type NavListProps = {
  items: NavItem[];
  /** Desktop rail collapsed to the 4rem icon strip. */
  collapsed?: boolean;
  /** Called after a link is chosen — the mobile drawer uses it to close. */
  onNavigate?: () => void;
};

/**
 * The one nav-list rendering shared by the desktop rail and the mobile
 * drawer. The drawer previously re-implemented this inline and had drifted
 * (different active treatment, no collapsed mode, its own group-header
 * markup); collapsing both onto this component gives a single source of
 * truth for active styling, group seams, and the hint tooltips.
 *
 * Renders Radix tooltips for collapsed icons and for any item carrying a
 * `hint`, so a TooltipProvider ancestor is required at every call site.
 *
 * Active items read as neutral fill + a teal seam bar rather than teal
 * label text: on the dark neutral palette `--primary` is a fill token, not
 * a text token, so `text-primary` here would fail contrast.
 */
export function NavList({ items, collapsed = false, onNavigate }: NavListProps) {
  const location = useLocation();
  const elements: ReactNode[] = [];
  let lastGroup: NavGroup | null = null;

  for (const item of items) {
    // Insert an eyebrow header when the group changes (only named groups).
    if (item.group !== lastGroup && GROUP_LABELS[item.group]) {
      elements.push(
        <div
          key={`group-${item.group}`}
          className={cn(
            "flex items-center gap-2 px-3 pt-4 pb-1",
            collapsed && "justify-center px-0 py-2",
          )}
        >
          {collapsed ? (
            // On the collapsed rail, replace the label with a thin divider.
            <div className="w-6 border-t border-border" />
          ) : (
            <span className="text-xs font-medium text-muted-foreground">
              {GROUP_LABELS[item.group]}
            </span>
          )}
        </div>,
      );
    }
    lastGroup = item.group;

    // Resolve active state ourselves rather than via NavLink's
    // `className` render-prop: hinted items (and every item on the
    // collapsed rail) are the `asChild` target of a Radix TooltipTrigger,
    // whose Slot stringifies a FUNCTION className instead of calling it —
    // silently dropping the active fill on those links (Contacts, Inbox,
    // …) while bare links kept it. A plain string survives that merge.
    // Mirrors AppShell's `activeItem`: exact path or a descendant of it.
    const isActive =
      location.pathname === item.to ||
      location.pathname.startsWith(`${item.to}/`);

    const link = (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={onNavigate}
        className={cn(
          "relative flex items-center rounded-md text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
          collapsed
            ? "justify-center px-2 py-2 [&_svg]:h-5 [&_svg]:w-5"
            : "min-h-touch gap-2.5 px-3 py-2 [&_svg]:h-4 [&_svg]:w-4",
          isActive
            ? cn(
                "bg-accent font-medium text-foreground before:absolute before:left-0 before:w-0.5 before:rounded-full before:bg-primary",
                collapsed ? "before:inset-y-1.5" : "before:inset-y-2",
              )
            : "text-foreground hover:bg-muted",
        )}
      >
        {item.icon}
        {!collapsed && <span>{item.label}</span>}
      </NavLink>
    );

    if (collapsed) {
      elements.push(
        <Tooltip key={item.to}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">
            {item.hint ? (
              <span>
                <span className="font-semibold">{item.label}</span> —{" "}
                {item.hint}
              </span>
            ) : (
              item.label
            )}
          </TooltipContent>
        </Tooltip>,
      );
    } else if (item.hint) {
      elements.push(
        <Tooltip key={item.to}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">{item.hint}</TooltipContent>
        </Tooltip>,
      );
    } else {
      elements.push(link);
    }
  }

  return <>{elements}</>;
}
